import {
    DatasetColumnDataType,
    DatasetColumnRole,
    DatasetType,
    MetricAggregationFunction,
    TimeGranularity,
} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Dataset} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {AnalyticsQueryVariant, type DataResolverProps, type RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import type {QueryClient} from "@tanstack/react-query";
import type {AnalyticalEngineDataTransformationContext} from "~/queries/analyticalEngine/analyticalEngineQueries";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {fetchXochipilliDatasetData} from "~/workspaceConfig/hostedPages/dashboard/-api";
import {replaceNullStringDimensionsWithOthers} from "~/workspaceConfig/hostedPages/dashboard/-nullDimensionValues";
import {DashboardRowType, type DashboardConfig} from "~/workspaceConfig/hostedPages/dashboard/-types";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";

export const TUFFEX_DASHBOARD_DATASET_ID = "495eb3b9-007d-44c1-aa03-5660798e8b34" as Uuid;
export const OTTOKART_GA4_LOGINS_DATASET_ID = "e90dde7d-74fa-447e-a038-c0e705827f0b" as Uuid;
const OTTOKART_GA4_TOTAL_LOGINS_METRIC_ID = "3c0f6ae8-96f2-4da6-810a-8191377b10df" as Uuid;
const OTTOKART_GA4_TOTAL_ACTIVE_USERS_METRIC_ID = "0e45143c-0c70-4664-8d5a-a5754dd08e0c" as Uuid;

const TUFFEX_METRIC_IDS = {
    averageQuotationValue: "2cc5e21c-2556-4ca3-ae5a-c962405a1472" as Uuid,
    totalQuotationValue: "e100a1cf-7090-4fc2-8545-fec0bac9ccda" as Uuid,
    leads: "73d91cfd-a899-4b31-9861-3ef04381c121" as Uuid,
    customerRegistrations: "9bd81915-db7a-4197-b307-347999d91c68" as Uuid,
    quotations: "ad4f8254-b555-492c-966d-c6c5155f929d" as Uuid,
} as const;

const TUFFEX_COMPUTED_METRIC_IDS = {
    quotationValuePerLead: "6f8e1c2a-1b4d-4a9f-8e3c-2d7b5a901234" as Uuid,
    leadToQuotationRatio: "7a9f2d3b-6c1e-4b8a-9d5f-3e7c1b204568" as Uuid,
} as const;

const TUFFEX_WIDGET_IDS = {
    averageQuotationValue: "b4ebb98b-18db-4683-8a6f-f840963e856a" as Uuid,
    leads: "57ff88b0-ff48-4ea5-b01c-7b66c3223af2" as Uuid,
    customerRegistrations: "5d3301d2-d41e-44a4-a443-01c00fe3a266" as Uuid,
    quotations: "442bdca5-6b13-40a2-81fd-5bb6f663c368" as Uuid,
    totalQuotationValue: "8c9d0e1f-2a3b-4456-c789-abcdef012345" as Uuid,
    totalLogins: "6a7b8c9d-0e1f-4234-a567-89abcdef0123" as Uuid,
    totalActiveUsers: "7b8c9d0e-1f2a-4345-b678-9abcdef01234" as Uuid,
} as const;

const TUFFEX_TREND_WIDGET_IDS = {
    leads: "b1c2d3e4-f5a6-4789-b012-3456789abcde" as Uuid,
    quotations: "c2d3e4f5-a6b7-4890-c123-456789abcdef" as Uuid,
    customerRegistrations: "e4f5a6b7-c8d9-4012-e345-6789abcdef01" as Uuid,
    totalLogins: "a6b7c8d9-e0f1-4234-a567-89abcdef0124" as Uuid,
    totalActiveUsers: "b7c8d9e0-f1a2-4345-b678-9abcdef01235" as Uuid,
    totalQuotationValue: "c8d9e0f1-a2b3-4456-8abc-def012345678" as Uuid,
} as const;

const TUFFEX_CHART_COLOR_PALETTE = ["#4D92E4"] as const;

type TuffexDashboardConfigArguments = {
    getAdditionalColumns: CapabilityUdoColumnResolver;
    dataset: Dataset;
    ga4Dataset: Dataset;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    transformationContext: AnalyticalEngineDataTransformationContext;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
};

const metricTitles = {
    averageQuotationValue: "Average Quotation Value",
    leads: "Total Leads",
    customerRegistrations: "Customer Registrations",
    quotations: "Total Quotations",
} as const;

const TUFFEX_WIDGET_GLOSSARIES = {
    totalActiveUsers: "Number of users who have had at least one session on the store.",
    totalCustomerRegistrations: "Total number of customers who registered first time on the store.",
    totalLeads: "Number of customers who have filled the lead form.",
    totalLogins: "Number of times users logged in to the store.",
    totalQuotations: "Number of quotations requested by customer and generated by the sales team.",
    totalQuotationValue: "Total value of all quotations generated.",
    averageQuotationValue:
        "Average value of a quotation, calculated by dividing Total Quotation Value by Total Quotations.",
    quotationValuePerLead:
        "Average quotation value generated per lead, calculated by dividing Total Quotation Value by Total Leads.",
    leadToQuotationRatio:
        "Percentage of leads that resulted in a quotation, calculated by dividing Total Quotations by Total Leads.",
    customerRegistrations: "Shows the number of customers who registered on the store for the first time.",
    activeUsersTrend: "Shows the number of users who have had at least one session on the store.",
    totalLoginsTrend: "Shows the number of user logins in each interval of the selected period.",
    leadsTrend: "Shows the number of new leads created in each interval of the selected period.",
    quotationsTrend: "Number of quotations requested by customer and generated by the sales team.",
    totalQuotationValueTrend: "Total value of all quotations generated.",
} as const;

export function getTuffexDashboardConfig({
    getAdditionalColumns,
    dataset,
    ga4Dataset,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    transformationContext,
    timeZone,
    startTime,
    endTime,
}: TuffexDashboardConfigArguments): DashboardConfig {
    if (dataset.id !== TUFFEX_DASHBOARD_DATASET_ID) {
        throw new Error(`Unexpected Tuffex dashboard dataset: ${dataset.id}`);
    }
    if (dataset.type !== DatasetType.periodic) {
        throw new Error("Tuffex dashboard dataset must be periodic to render date-based bar graphs.");
    }

    const dateColumn = dataset.columns.find(
        (column) =>
            column.role === DatasetColumnRole.dimension &&
            (column.dataType === DatasetColumnDataType.date || column.dataType === DatasetColumnDataType.dateTime),
    );
    if (dateColumn === undefined) {
        throw new Error("Tuffex dashboard dataset must contain a date or date-time dimension.");
    }

    const missingMetricIds = Object.values(TUFFEX_METRIC_IDS).filter((metricId) => dataset.columns.some((column) => column.id === metricId) !== true);
    if (missingMetricIds.length > 0) {
        throw new Error(`Tuffex dashboard dataset is missing metrics: ${missingMetricIds.join(", ")}`);
    }

    if (ga4Dataset.id !== OTTOKART_GA4_LOGINS_DATASET_ID || ga4Dataset.type !== DatasetType.periodic) {
        throw new Error("Ottokart GA4 logins dataset must be a periodic dataset with the expected id.");
    }
    const missingGa4MetricIds = [OTTOKART_GA4_TOTAL_LOGINS_METRIC_ID, OTTOKART_GA4_TOTAL_ACTIVE_USERS_METRIC_ID].filter(
        (metricId) => ga4Dataset.columns.some((column) => column.id === metricId) !== true,
    );
    if (missingGa4MetricIds.length > 0) {
        throw new Error(`Ottokart GA4 dataset is missing metrics: ${missingGa4MetricIds.join(", ")}`);
    }
    const ga4DateColumn = ga4Dataset.columns.find(
        (column) =>
            column.role === DatasetColumnRole.dimension &&
            (column.dataType === DatasetColumnDataType.date || column.dataType === DatasetColumnDataType.dateTime),
    );
    if (ga4DateColumn === undefined) {
        throw new Error("Ottokart GA4 dataset must contain a date or date-time dimension for the logins trend.");
    }

    const fetchDashboardData = async (sourceDataset: Dataset, props: DataResolverProps) => {
        const result = await fetchXochipilliDatasetData(
            getAdditionalColumns,
            intellsysBaseUrl,
            accessToken,
            workspaceId,
            queryClient,
            transformationContext,
            sourceDataset,
            props,
        );
        if (result.success !== true) {
            return result;
        }
        return dataResult(replaceNullStringDimensionsWithOthers(result.data));
    };

    const fetchData = (_resolvedDataset: Dataset, props: DataResolverProps) => fetchDashboardData(dataset, props);

    const getMetricTotal = (rawData: RawData, metricId: Uuid): number | null => {
        const metricColumn = rawData.columnMetadata.find((column) => column.id === metricId);
        if (metricColumn === undefined) {
            return null;
        }

        let total = 0;
        for (const row of rawData.dataPoints) {
            const value = row[metricColumn.columnIndex]?.value;
            if (value === null || value === undefined) {
                continue;
            }
            const numericValue = typeof value === "number" ? value : Number(value);
            if (Number.isFinite(numericValue)) {
                total += numericValue;
            }
        }
        return total;
    };

    const makeNumberTile = (
        id: Uuid,
        title: string,
        metric: Uuid,
        decimalPlaces: number,
        {
            valuePrefix = "",
            sourceDataset = dataset,
            glossary,
        }: {
            valuePrefix?: string;
            sourceDataset?: Dataset;
            glossary?: string;
        } = {},
    ): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: sourceDataset.id,
        datasetResolver: async () => dataResult(sourceDataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchDashboardData(sourceDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: true,
        valuePrefix,
        valueSuffix: "",
        ...(glossary === undefined ? {} : {glossary}),
    });

    const makeComputedNumberTile = ({
        id,
        title,
        syntheticMetricId,
        valuePrefix,
        valueSuffix,
        glossary,
        resolveValue,
    }: {
        id: Uuid;
        title: string;
        syntheticMetricId: Uuid;
        valuePrefix: string;
        valueSuffix: string;
        glossary?: string;
        resolveValue: (params: {props: DataResolverProps; fetch: (props: DataResolverProps) => Promise<Result<RawData>>}) => Promise<Result<number>>;
    }): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: [syntheticMetricId],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (resolvedDataset, props) => {
            const valueResult = await resolveValue({
                props,
                fetch: (innerProps) => fetchData(resolvedDataset, innerProps),
            });
            if (valueResult.success !== true) {
                return valueResult;
            }
            const synthetic: RawData = {
                columnMetadata: [
                    {
                        id: syntheticMetricId,
                        columnIndex: 0,
                        displayName: title,
                        dataType: DatasetColumnDataType.number,
                        role: DatasetColumnRole.metric,
                    },
                ],
                dataPoints: [[{value: valueResult.data}]],
            };
            return dataResult(synthetic);
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces: 2,
        spreadValue: true,
        valuePrefix,
        valueSuffix,
        ...(glossary === undefined ? {} : {glossary}),
    });

    const resolveMetricTotals = async ({
        props,
        fetch,
    }: {
        props: DataResolverProps;
        fetch: (props: DataResolverProps) => Promise<Result<RawData>>;
    }): Promise<Result<{totalQuotationValue: number; leads: number; quotations: number}>> => {
        const queryResult = await fetch({
            ...props,
            metrics: [
                {id: TUFFEX_METRIC_IDS.totalQuotationValue, aggregationFunction: MetricAggregationFunction.sum},
                {id: TUFFEX_METRIC_IDS.leads, aggregationFunction: MetricAggregationFunction.sum},
                {id: TUFFEX_METRIC_IDS.quotations, aggregationFunction: MetricAggregationFunction.sum},
            ],
            dimensions: [],
        } as DataResolverProps);
        if (queryResult.success !== true) {
            return queryResult;
        }

        const totalQuotationValue = getMetricTotal(queryResult.data, TUFFEX_METRIC_IDS.totalQuotationValue);
        const leads = getMetricTotal(queryResult.data, TUFFEX_METRIC_IDS.leads);
        const quotations = getMetricTotal(queryResult.data, TUFFEX_METRIC_IDS.quotations);
        if (totalQuotationValue === null || leads === null || quotations === null) {
            return errorResult(new Error("Ottokart derived KPI metrics are missing from the response"));
        }
        return dataResult({totalQuotationValue, leads, quotations});
    };

    const makeBarGraph = (
        id: Uuid,
        title: string,
        metric: Uuid,
        {
            sourceDataset = dataset,
            sourceDateColumn = dateColumn,
            glossary,
            metricLabel,
        }: {
            sourceDataset?: Dataset;
            sourceDateColumn?: Dataset["columns"][number];
            glossary?: string;
            metricLabel?: string;
        } = {},
    ): Widget => ({
        id,
        type: WidgetType.multiBarGraph,
        datasetId: sourceDataset.id,
        datasetResolver: async () => dataResult(sourceDataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [sourceDateColumn.id],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (_resolvedDataset, props) => {
            const result = await fetchDashboardData(sourceDataset, props);
            if (result.success !== true || metricLabel === undefined) {
                return result;
            }

            return dataResult({
                ...result.data,
                columnMetadata: result.data.columnMetadata.map((column) =>
                    column.id === metric ? {...column, displayName: metricLabel} : column,
                ),
            });
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
        chartColorPalette: TUFFEX_CHART_COLOR_PALETTE,
        granularityOptions: [TimeGranularity.daily, TimeGranularity.monthly, TimeGranularity.yearly],
        defaultGranularity: TimeGranularity.daily,
        ...(glossary === undefined ? {} : {glossary}),
    });

    return {
        id: uniqueUuid`f3a6d7c1-2e84-4b90-9c15-6d7e8f901234`,
        title: "Performance dashboard",
        sections: [
            {
                id: uniqueUuid`a1b2c3d4-e5f6-4789-a012-3456789abcde`,
                title: "Key Metrics",
                rows: [
                    {
                        title: "Top Funnel",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.totalActiveUsers,
                                "Total active users",
                                OTTOKART_GA4_TOTAL_ACTIVE_USERS_METRIC_ID,
                                0,
                                {
                                    sourceDataset: ga4Dataset,
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.totalActiveUsers,
                                },
                            ),
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.customerRegistrations,
                                "Total Customer Registrations",
                                TUFFEX_METRIC_IDS.customerRegistrations,
                                0,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.totalCustomerRegistrations},
                            ),
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.leads,
                                "Total Leads",
                                TUFFEX_METRIC_IDS.leads,
                                0,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.totalLeads},
                            ),
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.totalLogins,
                                "Total Logins",
                                OTTOKART_GA4_TOTAL_LOGINS_METRIC_ID,
                                0,
                                {sourceDataset: ga4Dataset, glossary: TUFFEX_WIDGET_GLOSSARIES.totalLogins},
                            ),
                        ],
                    },
                    {
                        title: "Bottom Funnel",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.quotations,
                                "Total Quotations",
                                TUFFEX_METRIC_IDS.quotations,
                                0,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.totalQuotations},
                            ),
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.totalQuotationValue,
                                "Total Quotation value",
                                TUFFEX_METRIC_IDS.totalQuotationValue,
                                2,
                                {
                                    valuePrefix: "₹",
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.totalQuotationValue,
                                },
                            ),
                            makeNumberTile(
                                TUFFEX_WIDGET_IDS.averageQuotationValue,
                                "Average Quotation value",
                                TUFFEX_METRIC_IDS.averageQuotationValue,
                                2,
                                {
                                    valuePrefix: "₹",
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.averageQuotationValue,
                                },
                            ),
                            makeComputedNumberTile({
                                id: uniqueUuid`8b1d4e6f-2a93-4c75-9e10-5f7b3d8a246c`,
                                title: "Quotation value per lead",
                                syntheticMetricId: TUFFEX_COMPUTED_METRIC_IDS.quotationValuePerLead,
                                valuePrefix: "₹",
                                valueSuffix: "",
                                glossary: TUFFEX_WIDGET_GLOSSARIES.quotationValuePerLead,
                                resolveValue: async ({props, fetch}) => {
                                    const totalsResult = await resolveMetricTotals({props, fetch});
                                    if (totalsResult.success !== true) {
                                        return totalsResult;
                                    }
                                    return dataResult(totalsResult.data.leads > 0 ? totalsResult.data.totalQuotationValue / totalsResult.data.leads : 0);
                                },
                            }),
                            makeComputedNumberTile({
                                id: uniqueUuid`9c2e5f7a-3b14-4d86-a021-6c8e4f9b357d`,
                                title: "Lead to Quotation ratio",
                                syntheticMetricId: TUFFEX_COMPUTED_METRIC_IDS.leadToQuotationRatio,
                                valuePrefix: "",
                                valueSuffix: "%",
                                glossary: TUFFEX_WIDGET_GLOSSARIES.leadToQuotationRatio,
                                resolveValue: async ({props, fetch}) => {
                                    const totalsResult = await resolveMetricTotals({props, fetch});
                                    if (totalsResult.success !== true) {
                                        return totalsResult;
                                    }
                                    return dataResult(totalsResult.data.leads > 0 ? (totalsResult.data.quotations / totalsResult.data.leads) * 100 : 0);
                                },
                            }),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`b2c3d4e5-f6a7-4890-b123-456789abcdef`,
                title: "Trends",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.leads,
                                metricTitles.leads,
                                TUFFEX_METRIC_IDS.leads,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.leadsTrend},
                            ),
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.quotations,
                                metricTitles.quotations,
                                TUFFEX_METRIC_IDS.quotations,
                                {
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.quotationsTrend,
                                    metricLabel: "Quotation Count",
                                },
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.totalActiveUsers,
                                "Total active users",
                                OTTOKART_GA4_TOTAL_ACTIVE_USERS_METRIC_ID,
                                {
                                    sourceDataset: ga4Dataset,
                                    sourceDateColumn: ga4DateColumn,
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.activeUsersTrend,
                                },
                            ),
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.customerRegistrations,
                                metricTitles.customerRegistrations,
                                TUFFEX_METRIC_IDS.customerRegistrations,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.customerRegistrations},
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.totalQuotationValue,
                                "Total Quotation value",
                                TUFFEX_METRIC_IDS.totalQuotationValue,
                                {glossary: TUFFEX_WIDGET_GLOSSARIES.totalQuotationValueTrend},
                            ),
                            makeBarGraph(
                                TUFFEX_TREND_WIDGET_IDS.totalLogins,
                                "Total Logins",
                                OTTOKART_GA4_TOTAL_LOGINS_METRIC_ID,
                                {
                                    sourceDataset: ga4Dataset,
                                    sourceDateColumn: ga4DateColumn,
                                    glossary: TUFFEX_WIDGET_GLOSSARIES.totalLoginsTrend,
                                },
                            ),
                        ],
                    },
                ],
            },
        ],
    };
}
